/**
 * Founder Control Center v2 · Shell layout unificado.
 *
 * Único punto de entrada del founder: /founder
 * Sidebar L1 con 6 secciones. Convergence Sprint · Feb 2026.
 *
 * Rutas:
 *   /founder              → Dashboard Principal (FounderHome reutilizado)
 *   /founder/dashboard    → alias del anterior
 *   /founder/intelligence → Insights · Auditorías · Knowledge · Funnel · Alertas · Copilot
 *   /founder/growth       → Content · A/B · Coupons · Affiliates · Reviews · SEO · Competitors
 *   /founder/operations   → Centros · Directorio · North Star · Pricing · Invoices · …
 *   /founder/agents       → 7 agentes IA (AG-01..AG-07) · sección preparada
 *   /founder/security     → Checklist · Event Log · 2FA · Compliance
 *
 * `/admin` legacy sigue accesible con banner de "Panel legacy".
 *
 * Solo superadmin tiene acceso (protegido en App.js).
 */
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  House, Lightning, ChartBar, Wrench, Robot, ShieldCheck, Cpu, Bell,
  SignOut, List as ListIcon, X, Compass,
} from "@phosphor-icons/react";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import { useTheme } from "../lib/theme";
import { useAuth } from "../lib/auth";
import NotificationInboxDropdown from "../components/notifications/NotificationInboxDropdown";

const NAV = [
  { to: "/founder",              label: "Dashboard Principal", icon: House,        end: true,
    description: "North Star · Salud · MRR · Funnel · Alertas" },
  { to: "/founder/intelligence", label: "Intelligence",        icon: Lightning,
    description: "Insights · Auditorías · Knowledge · Funnel · Alertas" },
  { to: "/founder/growth",       label: "Growth",              icon: ChartBar,
    description: "Content · A/B · Cupones · Afiliados · Reseñas" },
  { to: "/founder/operations",   label: "Operations",          icon: Wrench,
    description: "Centros · Pricing · Facturas · Catálogo · Brand" },
  { to: "/founder/agents",       label: "AI Agents",           icon: Robot,
    description: "7 agentes (AG-01..AG-07) · preparados Sprint A" },
  { to: "/founder/advisor",      label: "Advisor",             icon: Compass,
    description: "Founder Dashboard T-13 · Ecosistema AG-06 · solo lectura" },
  { to: "/founder/security",     label: "Security",            icon: ShieldCheck,
    description: "Checklist · Event Log · 2FA · Compliance" },
  { to: "/founder/automation",   label: "Automation",          icon: Cpu,
    description: "Engine T-16 · Jobs · Scheduler · Queue · DLQ · Metrics" },
  { to: "/founder/notifications", label: "Notifications",       icon: Bell,
    description: "Dispatcher T-17 · Deliveries · DLQ · Templates · Preferences" },
];

export default function FounderShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { logout: authLogout } = useAuth();

  const current = NAV.find(n =>
    n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
  ) || NAV[0];

  const handleLogout = async () => {
    try { await authLogout(); } catch { /* ignore */ }
    navigate("/login", { replace: true });
  };

  return (
    <div className={`min-h-screen flex bg-[var(--ag-bg)] text-[var(--ag-text)] ${theme === "dark" ? "dark-scope" : ""}`}
         data-testid="founder-shell">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 transform transition-transform
                    lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
                    bg-[var(--ag-surface)] border-r border-[var(--ag-border)] flex flex-col`}
        data-testid="founder-sidebar"
      >
        {/* Logo + tag */}
        <div className="p-5 border-b border-[var(--ag-border)] flex items-center justify-between gap-2">
          <Link to="/founder" className="shrink-0" data-testid="founder-sidebar-logo" aria-label="Founder Control Center">
            <AutoGlowBrand size={22} />
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-[var(--ag-text-dim)] hover:text-white p-1.5 rounded"
            aria-label="Cerrar menú"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-[var(--ag-border)]">
          <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Lightning size={11} weight="fill" /> Founder Control Center · v2
          </div>
          <div className="text-[10px] text-[var(--ag-text-dim)] mt-1 leading-tight">
            Único punto de entrada del founder
          </div>
        </div>

        {/* Nav · 6 secciones */}
        <nav className="flex-1 overflow-y-auto py-3" data-testid="founder-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-start gap-3 px-5 py-3 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/5 text-[var(--ag-text)]"
                    : "border-transparent text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] hover:bg-[var(--ag-midnight)]/30"
                }`
              }
              data-testid={`founder-nav-${item.to === "/founder" ? "dashboard" : item.to.split("/").pop()}`}
            >
              <item.icon size={18} weight="duotone" className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.label}</div>
                <div className="text-[10px] text-[var(--ag-text-muted)] mt-0.5 truncate">
                  {item.description}
                </div>
              </div>
            </NavLink>
          ))}
        </nav>

        {/* Footer · Logout (Fase 8: SuperAdmin legacy link eliminado · /founder es la única interfaz oficial. /admin sigue accesible por URL directa solo para emergencias.) */}
        <div className="p-3 border-t border-[var(--ag-border)] space-y-1.5">
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-xs text-[var(--ag-text-dim)] hover:text-red-400 hover:bg-[var(--ag-midnight)]/30 rounded transition-colors w-full text-left"
            data-testid="founder-logout"
          >
            <SignOut size={13} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Backdrop móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Topbar · hamburguesa solo en móvil · inbox visible siempre */}
        <header className="sticky top-0 z-20 bg-[var(--ag-surface)] border-b border-[var(--ag-border)]
                           h-12 flex items-center justify-between gap-3 px-4">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-[var(--ag-text)] p-1.5 rounded"
            aria-label="Abrir menú"
            data-testid="founder-mobile-menu"
          >
            <ListIcon size={20} />
          </button>
          <div className="flex items-center gap-2 text-[11px] text-[var(--ag-text-dim)] flex-1">
            <current.icon size={14} weight="duotone" /> {current.label}
          </div>
          <NotificationInboxDropdown />
        </header>

        <Outlet />
      </main>
    </div>
  );
}
